import DefaultCover from '@assets/imgs/default_cover.png';
import { useEventGray } from '@hooks/useEventGray';
import { Button, Modal, ModalProps } from '@tencent/tea-component';
import beaconReport from '@utils/beacon';
import { openPcLobbyPage } from '@utils/concat-meeting-util';
import { getBetweenDate } from '@utils/parse-date';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CanvasPosterDialog } from './CanvasPosterDialog';
import { CopyLinkDialog, QrCodeDialog } from './CopyLinkDialog';
import { PosterDialog } from './PosterDialog';
import './index.scss';
import { useRegisterDetail } from '@model/event-register';
import { DashboardEventInfoRes } from '@quickService/DashboardEventInfo';
import { TimeIcon, ProhibitIcon, SucIcon } from './img/icons';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { useHostRegistrationUrl } from '@hooks/useRainbowConfig';

interface ShareActivityModalProps extends ModalProps {
  data: DashboardEventInfoRes['data'] & { short_link: string; meeting_title: string };
  hasFooter?: boolean;
  userName?: string;
  publishSuccess?: boolean;
  reviewSuccess?: boolean;
  publishTime?: string;
}
type ShareBtn = 'link' | 'qrcode' | 'poster' | 'canvas';

const ShareActiveModal: React.FC<ShareActivityModalProps> = (props) => {
  const {
    visible,
    onClose,
    caption = '分享活动',
    data,
    hasFooter,
    userName,
    publishSuccess,
    publishTime,
    reviewSuccess,
  } = props;
  const { lobbySwitch } = useEventGray({ event_id: data?.event_id });
  const { data: registerDetail } = useRegisterDetail(props.data?.event_id);
  const registrationUrl = useHostRegistrationUrl(registerDetail?.host_id);
  const router = useRouter();
  const isBlacklist = useMemo(() => data?.is_person_audit === 2, [data]);
  const newModalStyle = useMemo(
    () => (data?.is_person_audit === 1 && data?.plaza_status === 0) || data?.is_person_audit === 2,
    [data],
  );
  const shareUrl = useMemo(() => {
    if (data?.event_type === 0 && !data?.short_link)
      return registrationUrl.replace('[eventId]', data?.event_id);
    return data?.short_link;
  }, [data?.event_type, data?.short_link, data?.event_id, registrationUrl]);

  const reviewDesc = useMemo(() => {
    if (publishSuccess) {
      if (reviewSuccess) {
        return '审核通过，活动将被推荐至活动广场';
      }
      return `预计${publishTime
        ? dayjs.unix(Number(publishTime)).minute(0).add(2, 'day').format('MM-DD H:mm')
        : '--'
        }前完成审核`;
    }
    return '审核通过后活动将被推荐至活动广场';
  }, [publishSuccess, reviewSuccess, publishTime]);

  const backToList = useCallback(() => {
    window.open(`${window.location.origin}/${router.basePath}/home`, '_blank');
  }, []);

  const toPreview = useCallback(() => {
    window.open(shareUrl);
  }, [shareUrl]);

  const entryLobby = useCallback(() => {
    openPcLobbyPage(data?.event_id);
  }, [data?.event_id]);

  useEffect(() => {
    if (visible) {
      beaconReport('e#event_share_popup#all#exposure');
    }
  }, [visible]);

  const [secondaryDialog, setSecondary] = useState<ShareBtn>();

  const onSecondClose = useCallback(() => {
    onClose?.();
    setSecondary(undefined);
  }, []);

  const onSecondBack = useCallback(() => {
    setSecondary(undefined);
  }, []);

  return (
    <>
      <Modal
        onClose={onClose}
        visible={visible && !secondaryDialog}
        caption={caption}
        className={classNames('share-activity-modal', {
          'publish-active-modal': hasFooter,
          success: publishSuccess,
          'new-modal-style': newModalStyle,
          'review-success': reviewSuccess,
        })}
      >
        <Modal.Body>
          <div className="share-activity-modal-body">
            <div className="share-activity-header">
              <div className="cover">
                <img src={data?.cover_url || DefaultCover} alt="" />
              </div>
              <div className="detail">
                <div className="title">{data?.meeting_title || data?.title}</div>
                <div className="time">
                  <TimeIcon className="icon_time" />
                  <div className="text">
                    {getBetweenDate(data?.start_time, data?.end_time).replace(/-/g, ' - ')}
                  </div>
                </div>
              </div>
            </div>
            <div className="division"></div>
            <div className="publish-status">
              <div className="publish-status-progress">
                <div className="publish-status-progress-icon">
                  <div className="icon_clock clock1">
                    <div className="time hour" />
                    <div className="time min" />
                  </div>
                  <SucIcon className="icon_suc suc1" />
                </div>
                <div className="publish-status-progress-line1">
                  <div className="progress-bar" />
                </div>
                <div className="publish-status-progress-icon">
                  {isBlacklist ? (
                    <ProhibitIcon />
                  ) : (
                    <>
                      <div className="circle" />
                      <div className="icon_clock clock2">
                        <div className="time hour" />
                        <div className="time min" />
                      </div>
                      <SucIcon className="icon_suc suc2" />
                    </>
                  )}
                </div>
                <div className="publish-status-progress-line2">
                  <div className="progress-bar" />
                </div>
              </div>
              <div className="publish-status-content">
                <div className="content-box">
                  <div className="title">活动发布</div>
                  <div className="desc">
                    {publishSuccess
                      ? '发布成功，您可以通过以下方式分享活动'
                      : '发布成功后您可以分享活动邀请观众报名'}
                  </div>
                </div>
                <div className="content-box">
                  <div className="title">上架活动广场审核</div>
                  <div className="desc">{reviewDesc}</div>
                </div>
              </div>
            </div>
            <div className="share-activity-share-content">
              <div className="empty-element" />
              <div className="share-activity-share-title">通过以下方式分享活动:</div>
              <div className="share-activity-share-btns">
                <div className="share-activity-share-btns-btn" onClick={() => setSecondary('link')}>
                  <div className="icon">
                    <i className="iconfont icon-link2" />
                  </div>
                  <div className="share-activity-share-btns-btn-text">邀请链接</div>
                </div>
                <div
                  className="share-activity-share-btns-btn"
                  onClick={() => setSecondary('qrcode')}
                >
                  <div className="icon">
                    <i className="iconfont icon-qrcode-1" />
                  </div>
                  <div className="share-activity-share-btns-btn-text">二维码</div>
                </div>
                <div
                  className="share-activity-share-btns-btn"
                  onClick={() => setSecondary('poster')}
                >
                  <div className="icon">
                    <i className="iconfont icon-img" />
                  </div>
                  <div className="share-activity-share-btns-btn-text">海报</div>
                </div>
                {registerDetail?.custom_mode === 1 ? (
                  <div
                    className="share-activity-share-btns-btn"
                    onClick={() => setSecondary('canvas')}
                  >
                    <div className="icon">
                      <i className="iconfont icon-poster" />
                    </div>
                    <div className="share-activity-share-btns-btn-text">长图</div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </Modal.Body>
        {hasFooter && (
          <Modal.Footer>
            <div className="share-activity-modal-footer">
              <Button type="weak" className="btn" onClick={backToList}>
                返回活动列表
              </Button>
              {newModalStyle && !publishSuccess ? null : (
                <>
                  {lobbySwitch && (
                    <Button type="weak" className="btn" onClick={entryLobby}>
                      进入活动大厅
                    </Button>
                  )}
                  <Button type="primary" className="btn" onClick={toPreview}>
                    查看活动详情
                  </Button>
                </>
              )}
            </div>
          </Modal.Footer>
        )}
      </Modal>
      <CopyLinkDialog
        event={data}
        userName={userName}
        visible={secondaryDialog === 'link'}
        onClose={onSecondClose}
        onBack={onSecondBack}
        registrationUrl={shareUrl}
      />
      <QrCodeDialog
        event={data}
        userName={userName}
        visible={secondaryDialog === 'qrcode'}
        onClose={onSecondClose}
        onBack={onSecondBack}
        registrationUrl={shareUrl}
      />
      <PosterDialog
        event={data}
        visible={secondaryDialog === 'poster'}
        onClose={onSecondClose}
        onBack={onSecondBack}
        registrationUrl={shareUrl}
      />
      <CanvasPosterDialog
        event={data}
        userName={userName}
        visible={secondaryDialog === 'canvas'}
        onClose={onSecondClose}
        onBack={onSecondBack}
        registrationUrl={shareUrl}
      />
    </>
  );
};

ShareActiveModal.displayName = 'ShareActiveModal';

export default ShareActiveModal;
